import { useFormikContext } from "formik";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { useMemo } from "react";

function FormErrorSummary() {
  const { errors, touched, submitCount } = useFormikContext();

  const visibleErrors = useMemo(() => {
    const errorEntries: Array<{ field: string; message: string }> = [];

    function collectErrors(obj: any, parentKey = "", touchedObj: any = {}) {
      for (const [key, value] of Object.entries(obj)) {
        const fullKey = parentKey ? `${parentKey}.${key}` : key;
        const isTouched = touchedObj[key];

        if (typeof value === "string" && isTouched) {
          errorEntries.push({
            field: fullKey,
            message: value,
          });
        } else if (typeof value === "object" && value !== null && isTouched) {
          collectErrors(value, fullKey, touchedObj[key] || {});
        }
      }
    }

    collectErrors(errors, "", touched);
    return errorEntries;
  }, [errors, touched]);

  // Only a submit attempt earns the summary — a single blurred field shows its
  // own error, and a banner appearing mid-edit shifts everything below it.
  if (submitCount === 0 || visibleErrors.length === 0) {
    return null;
  }

  const getFieldLabel = (field: string): string => {
    const labelMap: Record<string, string> = {
      available_from_date: "Available from",
      available_to_date: "Available to",
      fixed_latest_departure_date: "Fixed latest departure date",
      "departureOffice.connect": "From",
      "deliveryOffice.connect": "To",
      "supplier.connect": "Supplier",
      "vehicle.connect": "Vehicle",
      "supplierInsuranceOption.connect": "Insurance",
      quantity: "Quantity",
      hire_unit_rate: "Relocation rate",
      hire_units_allowed: "Relocation hire",
      minimum_hire_units: "Minimum hire",
      extra_hire_units_allowed: "Extra hire",
      extra_hire_unit_rate: "Extra day rate",
      booking_fee_amount: "Booking fee",
      holding_deposit_amount: "Holding deposit",
      distance_allowed: "Distance allowed",
      "line.upsert.reference": "License plate(s)",
    };

    return (
      labelMap[field] ||
      field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())
    );
  };

  return (
    <div className="col-span-full mb-6 rounded-md border border-red-200 bg-red-50 p-4">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800">
            Please fix the following errors:
          </h3>
          <div className="mt-2">
            <ul className="list-disc space-y-1 pl-5 text-sm text-red-700">
              {visibleErrors.map(({ field, message }, index) => (
                <li key={index}>
                  <strong>{getFieldLabel(field)}:</strong> {message}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export { FormErrorSummary };
